import React, { useState, useEffect, useCallback } from 'react';
import { touristClientsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  FiRefreshCw, FiAlertCircle, FiPlus, FiX, FiEdit2, FiSearch,
} from 'react-icons/fi';
import LessonsPanel from './LessonsPanel';
import ClientStepsPanel from './ClientStepsPanel';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

// Stages of a leisure/visit traveller, in order (mirrors
// tourist_clients.models.TouristClient.STATUS_CHOICES). The hint is what
// "done" means for that stage, shown under the Status dropdown.
const STATUSES = [
  ['NEW', 'New enquiry', '#011ff3', 'Enquiry received — not spoken to yet.'],
  ['CONTACTED', 'Consultation done', '#8b5cf6', 'Destination, dates, number of travellers, budget and visa needs confirmed.'],
  ['QUOTED', 'Itinerary & quote sent', '#a855f7', 'Day-by-day itinerary and price sent to the client.'],
  ['CONFIRMED', 'Quote accepted / deposit paid', '#d946ef', 'Client accepted the quote and paid the deposit.'],
  ['DOCS_COLLECTED', 'Visa documents collected', '#ec4899', 'Passport (6+ months valid), photos, bank statements, hotel and return-flight reservations, travel insurance; invitation letter if visiting someone. Skip the visa stages if no visa is needed.'],
  ['VISA_APPLIED', 'Visa applied', '#f59e0b', 'Online pre-application done and appointment attended at the embassy/visa centre.'],
  ['VISA_APPROVED', 'Visa approved', '#84cc16', 'Visa issued. Check the dates and number of entries match the trip.'],
  ['BOOKED', 'Travel booked', '#22c55e', 'Flights, accommodation, airport transfers and insurance paid and confirmed; tickets sent to the client.'],
  ['TRAVELLING', 'On the trip', '#14b8a6', 'Client has departed and is at the destination.'],
  ['COMPLETED', 'Trip completed', '#10b981', 'Back home safely; feedback collected.'],
  ['CANCELLED', 'Cancelled / visa refused', '#f5576c', 'Trip cancelled or visa refused. Record why in Notes.'],
];

// Same choices as Medical (tourist_clients.models.TouristClient.PAYMENT_STATUS_CHOICES).
const PAYMENT_STATUSES = [
  ['NOT_PAID', 'Not Paid', '#f5576c'],
  ['PARTIAL', 'Partial', '#f5a623'],
  ['PAID', 'Paid', '#43e97b'],
  ['REFUNDED', 'Refunded', '#999'],
];
const paymentColor = (s) => (PAYMENT_STATUSES.find((x) => x[0] === s) || [null, null, '#999'])[2];

const statusColor = (s) => (STATUSES.find((x) => x[0] === s) || [null, null, '#999'])[2];
const statusLabel = (s) => (STATUSES.find((x) => x[0] === s) || [null, s])[1];
const statusHint = (s) => (STATUSES.find((x) => x[0] === s) || [])[3] || '';
// "Step 5 of 11" — CANCELLED is an outcome, not a step on the journey.
const JOURNEY = STATUSES.filter((x) => x[0] !== 'CANCELLED');
const stepNo = (s) => JOURNEY.findIndex((x) => x[0] === s) + 1; // 0 = not a step
const statusStep = (s) => (stepNo(s) ? `Step ${stepNo(s)} of ${JOURNEY.length}` : '');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const ClientModal = ({ initial, agents, onSave, onCancel, loading }) => {
  const isEdit = !!initial?.id;
  const { canSetPaymentStatus } = useAuth();
  const canSetPayment = canSetPaymentStatus();
  const [form, setForm] = useState({
    first_name: initial?.first_name || '',
    last_name: initial?.last_name || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    status: initial?.status || 'NEW',
    assigned_agent: initial?.assigned_agent || '',
    country: initial?.country || '',
    destination_country: initial?.destination_country || '',
    purpose: initial?.purpose || '',
    travel_date: initial?.travel_date || '',
    trip_duration_days: initial?.trip_duration_days ?? '',
    travelers_count: initial?.travelers_count ?? '',
    needs_visa_assistance: initial?.needs_visa_assistance || false,
    payment_status: initial?.payment_status || 'NOT_PAID',
    total_service_fee: initial?.total_service_fee ?? '',
    notes: initial?.notes || '',
  });
  const [collaboratorIds, setCollaboratorIds] = useState(
    (initial?.assigned_agents_detail || []).map((a) => String(a.id))
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCollaborator = (id) => setCollaboratorIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );
  const canSave = form.first_name.trim() && form.last_name.trim() && form.email.trim() && !loading;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div>
            <h2>{isEdit ? 'Edit Tourist' : 'New Tourist'}</h2>
            {isEdit && <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>{initial?.tourist_id}</div>}
          </div>
          <button className="modal-close" onClick={onCancel}><FiX /></button>
        </div>

        <div className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                {STATUSES.map(([v, l]) => (
                  <option key={v} value={v}>{stepNo(v) ? `${stepNo(v)}. ` : ''}{l}</option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>
                {statusStep(form.status) && <strong>{statusStep(form.status)}: </strong>}
                {statusHint(form.status)}
              </div>
            </div>
            <div className="form-group">
              <label>Assigned Agent</label>
              <select value={form.assigned_agent} onChange={(e) => set('assigned_agent', e.target.value)}>
                <option value="">— none —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.displayName || a.username}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Also assign (co-workers who can also see this record)</label>
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 8, maxHeight: 130, overflowY: 'auto' }}>
              {agents.filter((a) => String(a.id) !== String(form.assigned_agent)).map((a) => (
                <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" style={{ width: 'auto' }}
                    checked={collaboratorIds.includes(String(a.id))}
                    onChange={() => toggleCollaborator(String(a.id))} />
                  {a.displayName || a.username}
                </label>
              ))}
              {agents.length === 0 && <div style={{ color: '#999', fontSize: 12 }}>No agents/employees to add.</div>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Home Country</label>
              <input value={form.country} onChange={(e) => set('country', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Destination Country</label>
              <input value={form.destination_country} onChange={(e) => set('destination_country', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>Purpose of Trip</label>
            <input value={form.purpose} onChange={(e) => set('purpose', e.target.value)} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Travel Date</label>
              <input type="date" value={form.travel_date || ''} onChange={(e) => set('travel_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Trip Duration (days)</label>
              <input type="number" min="0" value={form.trip_duration_days} onChange={(e) => set('trip_duration_days', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label># Travelers</label>
              <input type="number" min="1" value={form.travelers_count} onChange={(e) => set('travelers_count', e.target.value)} />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={form.needs_visa_assistance} onChange={(e) => set('needs_visa_assistance', e.target.checked)} />
                Needs visa assistance
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Payment Status</label>
              <select value={form.payment_status} disabled={!canSetPayment} onChange={(e) => set('payment_status', e.target.value)}>
                {PAYMENT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {!canSetPayment && <small style={{ color: '#64748b' }}>Only the CFO, Chairman, CEO or COO changes this.</small>}
            </div>
            <div className="form-group">
              <label>Total Service Fee (USD)</label>
              <input type="number" min="0" step="0.01" value={form.total_service_fee} onChange={(e) => set('total_service_fee', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} />
          </div>
        </div>

        {isEdit && <ClientStepsPanel kind="TOURIST" clientId={initial.id} leadSource={initial.lead_source} leadPaid={initial.lead_paid} />}
        {isEdit && <LessonsPanel kind="tourist" clientId={initial.id} />}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!canSave}
            onClick={() => onSave({
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              email: form.email.trim(),
              phone: form.phone || '',
              status: form.status,
              assigned_agent: form.assigned_agent ? parseInt(form.assigned_agent, 10) : null,
              assigned_agent_ids: collaboratorIds.map((id) => parseInt(id, 10)),
              country: form.country || '',
              destination_country: form.destination_country || '',
              purpose: form.purpose || '',
              travel_date: form.travel_date || null,
              trip_duration_days: form.trip_duration_days === '' ? null : form.trip_duration_days,
              travelers_count: form.travelers_count === '' ? null : form.travelers_count,
              needs_visa_assistance: form.needs_visa_assistance,
              payment_status: form.payment_status,
              total_service_fee: form.total_service_fee === '' ? null : form.total_service_fee,
              notes: form.notes || '',
            })}
          >
            {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Tourist'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TouristClients = () => {
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ordering: '-created_at' };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await touristClientsAPI.list(params);
      setClients(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load tourists.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  const loadAgents = useCallback(async () => {
    try {
      const [agentsRes, employeesRes] = await Promise.all([
        usersAPI.agents(),
        usersAPI.permanentEmployees().catch(() => ({ data: [] })),
      ]);
      const agentsData = agentsRes.data || [];
      const employeesData = employeesRes.data || [];
      setAgents([
        ...agentsData.map((a) => ({ ...a, displayName: `${a.username} (Agent)` })),
        ...employeesData.map((e) => ({ ...e, displayName: `${e.username} (Employee)` })),
      ]);
    } catch {
      setAgents([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAgents(); }, [loadAgents]);

  const handleSave = async (data) => {
    setActionLoading(true);
    try {
      if (modal?.id) await touristClientsAPI.update(modal.id, data);
      else await touristClientsAPI.create(data);
      setModal(null);
      load();
    } catch (e) {
      setError(JSON.stringify(e?.response?.data) || 'Failed to save tourist.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="students-page">
      <div className="page-header">
        <div className="header-actions-left">
          <button className="btn-primary" onClick={() => setModal({})}>
            <FiPlus /> New Tourist
          </button>
        </div>
        <div className="header-actions-right">
          <div className="search-box">
            <FiSearch />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, email, phone, tourist ID…" />
          </div>
          <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className="btn-icon" onClick={load} title="Refresh">
            <FiRefreshCw className={loading ? 'cv-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiAlertCircle size={16} />
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={14} /></button>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="empty-state">No tourists found.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Tourist ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Destination</th>
                <th>Payment</th>
                <th>Agent</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} onClick={() => setModal(c)} style={{ cursor: 'pointer' }}>
                  <td><span className="cv-id">{c.tourist_id}</span></td>
                  <td>
                    <strong>{c.full_name || `${c.first_name} ${c.last_name}`}</strong>
                    <div style={{ color: '#999', fontSize: 12 }}>{c.email}</div>
                  </td>
                  <td>
                    <span className="status-pill" style={{ color: statusColor(c.status) }}>
                      {c.status_display || statusLabel(c.status)}
                    </span>
                    {statusStep(c.status) && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{statusStep(c.status)}</div>
                    )}
                  </td>
                  <td>{c.destination_country || '—'}</td>
                  <td><span style={{ color: paymentColor(c.payment_status), fontWeight: 600, fontSize: 12 }}>{c.payment_status_display || '—'}</span></td>
                  <td>{c.assigned_agent_name || '—'}</td>
                  <td>{fmtDate(c.created_at)}</td>
                  <td>
                    <button className="btn-icon" onClick={(e) => { e.stopPropagation(); setModal(c); }} title="Edit">
                      <FiEdit2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ClientModal
          agents={agents}
          initial={modal.id ? modal : null}
          onSave={handleSave}
          onCancel={() => setModal(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default TouristClients;
